import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Nav from "./Nav";
import Footer from "./Footer";
import "./home.css";
import "./Sales.css";
import "../Components/CustomModal.css";
import StandardPageMain from "../Components/StandardCardMain";
import SaleQuoteLines from "../Components/SaleQuoteLines";
import ErrorMessage from "../Components/ErrorPopup";
import SuccessMessage from "../Components/SuccessPopup";
import ConfirmationDialog from "../Components/Confirmation";
import useFetch from "../Components/UseFetch";
import { cleanSpecialNaming, parseApiError } from "../HelperFx/common.js";
import {
  companyDefault,
  confirmQuoteProcessing,
  printSalesQuotes,
  reopenQuote,
  salesQuotes as SQ,
} from "../HelperFx/datalinks";

const REACT_UNAME = process.env.REACT_APP_API_USERNAME;
const REACT_PWD = process.env.REACT_APP_API_PASSWORD;
const REACT_DATA_URL = process.env.REACT_APP_API_DATA_URL;
const REACT_ACTION_URL = process.env.REACT_APP_ACTION_PI_DATA_URL;

const SALES_QUOTE_TABLE_ID = 36;

const toDateInput = (value) => {
  if (!value) return "";
  const text = String(value);
  if (text.includes("0001-01-01") || text.startsWith("0001-")) return "";
  return text.slice(0, 10);
};

const getConfirmValue = (record) => {
  if (!record) return undefined;
  if (record.ConfirmProcessing !== undefined) return record.ConfirmProcessing;
  if (record.confirmProcessing !== undefined) return record.confirmProcessing;
  if (record.confirm !== undefined) return record.confirm;
  return undefined;
};

const formatConfirmStatus = (record) => {
  const value = getConfirmValue(record);
  if (value === true || value === 1) return "Yes";
  if (value === false || value === 0 || value == null || value === "") return "No";
  return cleanSpecialNaming(String(value));
};

const isConfirmed = (record) => {
  const value = getConfirmValue(record);
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === "") return false;
  const text = String(value).trim().toLowerCase();
  return !["no", "false", "0", "open", "n"].includes(text);
};

const mapHeader = (record) => ({
  ...record,
  documentNo: record.number,
  documentType: record.documentType || "Quote",
  status: isConfirmed(record) ? "Confirmed" : "Open",
  documentDate: toDateInput(record.documentDate),
  ConfirmProcessing: getConfirmValue(record),
  statusText: formatConfirmStatus(record),
  cancelReason: record.cancelReason || "",
});

const PdfPreview = ({ url, title, onClose }) => {
  if (!url) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "90vw", maxWidth: "1100px", height: "88vh" }}
      >
        <div className="modal-header">
          <h3>{title}</h3>
          <button onClick={onClose} className="modal-close">
            &times;
          </button>
        </div>
        <div className="modal-body" style={{ height: "calc(100% - 70px)" }}>
          <iframe
            src={url}
            title={title}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        </div>
      </div>
    </div>
  );
};

const SaleQuote = () => {
  const location = useLocation();
  const token = btoa(`${REACT_UNAME}:${REACT_PWD}`);
  const docNo = location.state?.number || location.state?.createdNumber || "";
  const [header, setHeader] = useState({});
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [printing, setPrinting] = useState(false);

  const headerUrl = docNo
    ? `${REACT_DATA_URL}${SQ}?${companyDefault}&$filter=number eq '${docNo}'`
    : null;
  const { data: headerResponse, error: headerFetchError } = useFetch(
    headerUrl || `${REACT_DATA_URL}${SQ}?${companyDefault}&$top=0`,
    token,
  );

  useEffect(() => {
    const record = headerResponse?.value?.[0];
    if (record) {
      setHeader((prev) => mapHeader({ ...record, cancelReason: prev.cancelReason }));
    }
  }, [headerResponse]);

  useEffect(() => {
    if (headerFetchError) {
      setErrorMessage(
        typeof headerFetchError === "string"
          ? headerFetchError
          : "Unable to load the quotation header.",
      );
      setErrorOpen(true);
    }
  }, [headerFetchError]);

  const showError = (message) => {
    setErrorMessage(message);
    setErrorOpen(true);
  };

  const handlePageBlur = async (field, value) => {
    if (field === "cancelReason") {
      setHeader((prev) => ({ ...prev, cancelReason: value }));
      return;
    }

    if (field !== "mou") return;
    if (isConfirmed(header)) return;

    const systemId = header.id || header.systemId;
    if (!systemId) {
      showError("The quotation header has not loaded yet.");
      return;
    }

    try {
      const response = await axios.patch(
        `${REACT_DATA_URL}${SQ}(${systemId})?${companyDefault}`,
        { mou: value },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${token}`,
            "If-Match": "*",
          },
        },
      );
      setHeader((prev) =>
        mapHeader({ ...response.data, cancelReason: prev.cancelReason }),
      );
    } catch (err) {
      showError(parseApiError(err));
    }
  };

  const refreshHeader = useCallback(async () => {
    if (!headerUrl) return;
    const refreshed = await axios.get(headerUrl, {
      headers: { Authorization: `Basic ${token}` },
    });
    const record = refreshed.data?.value?.[0];
    if (record) {
      setHeader((prev) => mapHeader({ ...record, cancelReason: prev.cancelReason }));
    }
  }, [headerUrl, token]);

  const runAction = useCallback(
    async (actionName, body, successText) => {
      try {
        const response = await axios.post(
          `${REACT_ACTION_URL}${actionName}?${companyDefault}`,
          body,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${token}`,
            },
          },
        );
        if (response.status >= 400) {
          throw new Error(response.statusText);
        }
        const confirmedNow = actionName === confirmQuoteProcessing;
        setHeader((prev) =>
          mapHeader({
            ...prev,
            ConfirmProcessing: confirmedNow,
            confirmProcessing: confirmedNow,
            confirm: confirmedNow,
            cancelReason: prev.cancelReason,
          }),
        );
        setSuccessMessage(successText);
        setSuccessOpen(true);
        await refreshHeader();
      } catch (err) {
        showError(parseApiError(err));
      }
    },
    [refreshHeader, token],
  );

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const response = await axios.post(
        `${REACT_ACTION_URL}${printSalesQuotes}?${companyDefault}`,
        { quoteNo: docNo },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${token}`,
          },
        },
      );
      const base64Data = response.data?.value;
      if (!base64Data) {
        throw new Error("The print service did not return a PDF.");
      }
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: "application/pdf",
      });
      setPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      showError(parseApiError(err));
    } finally {
      setPrinting(false);
    }
  };

  const askConfirm = (caption, message, onConfirm) => {
    setConfirmConfig({ caption, message, onConfirm });
    setConfirmOpen(true);
  };

  const confirmed = isConfirmed(header);
  const fields = [
    { name: "number", label: "Document No.", type: "text", disabled: true },
    { name: "customerNo", label: "Customer No.", type: "text", disabled: true },
    { name: "customerName", label: "Customer Name", type: "text", disabled: true },
    { name: "clientPhoneNo", label: "Client Phone Number", type: "text", disabled: true },
    { name: "billToAddress1", label: "Address", type: "text", disabled: true },
    { name: "email", label: "Customer Email", type: "email", disabled: true },
    { name: "documentDate", label: "Document Date", type: "date", disabled: true },
    { name: "description", label: "Description", type: "text", disabled: true },
    { name: "additionalDescription", label: "Additional Description", type: "text", disabled: true },
    { name: "reasonForQuotation", label: "Reason For Quotation", type: "text", disabled: true },
    {
      name: "mou",
      label: "Memorandum Of Understanding",
      type: "text",
      disabled: confirmed,
    },
    { name: "currencyCode", label: "Currency Code", type: "text", disabled: true },
    { name: "amount", label: "Amount", type: "decimal", format: "amount", disabled: true },
    { name: "statusText", label: "Processing Confirmed", type: "text", disabled: true },
    { name: "reasonForCancellation", label: "Reason For Cancellation", type: "text", disabled: true },
    {
      name: "cancelReason",
      label: "Cancel Quote Reason",
      type: "select",
      options: [
        { value: "1", label: "High Price" },
        { value: "2", label: "Change in Market" },
        { value: "3", label: "Low Budget" },
      ],
    },
  ];

  const actions = useMemo(
    () => [
      {
        name: printing ? "Generating..." : "Print",
        fx: handlePrint,
      },
      confirmed
        ? {
            name: "Cancel Quote",
            fx: () => {
              if (!header.cancelReason) {
                showError("Select a cancel quote reason before cancelling.");
                return;
              }
              askConfirm(
                "Cancel Quote",
                "Do you want to cancel this quotation?",
                () =>
                  runAction(
                    reopenQuote,
                    { docNo, cancelReason: header.cancelReason },
                    "Quote cancelled successfully.",
                  ),
              );
            },
          }
        : {
            name: "Confirm Processing",
            fx: () =>
              askConfirm(
                "Confirm Processing",
                "Do you want to confirm the quote processing?",
                () =>
                  runAction(
                    confirmQuoteProcessing,
                    { docNo },
                    "Quote confirmed.",
                  ),
              ),
          },
    ],
    [confirmed, docNo, header.cancelReason, printing, runAction],
  );

  if (!docNo) {
    return (
      <div className="Home">
        <Nav />
        <div className="dash-main">
          <div className="squote">
            <div className="firsttab px-4">Purchase Quote</div>
            <p style={{ padding: 20 }}>No quotation number was provided.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="Home">
      <Nav />
      <div className="dash-main">
        <div className="squote">
          <div className="firsttab px-4">Purchase Quote {docNo}</div>
          <div className="listTable">
            <StandardPageMain
              title={`Purchase Quote - ${docNo}`}
              fields={fields}
              initialData={header}
              actions={actions}
              redirectTo="PurchaseQuotes"
              onBlur={handlePageBlur}
              tableId={SALES_QUOTE_TABLE_ID}
              PreventEdit={false}
              authToken={token}
              attachmentApi={`${REACT_DATA_URL}documentAttachments`}
              onAttachmentError={showError}
            />
            <SaleQuoteLines
              docNo={docNo}
              token={token}
              allowEdit={!confirmed}
              onLinesChanged={refreshHeader}
            />
            <ErrorMessage
              isOpen={errorOpen}
              toggle={() => setErrorOpen(false)}
              caption="Error"
              message={errorMessage}
            />
            <SuccessMessage
              isOpen={successOpen}
              toggle={() => setSuccessOpen(false)}
              caption="Success"
              message={successMessage}
            />
            <ConfirmationDialog
              isOpen={confirmOpen}
              toggle={() => setConfirmOpen(false)}
              caption={confirmConfig.caption}
              message={confirmConfig.message}
              onConfirm={() => {
                confirmConfig.onConfirm?.();
                setConfirmOpen(false);
              }}
            />
            <PdfPreview
              url={pdfUrl}
              title={`PDF Preview - ${docNo}`}
              onClose={() => {
                if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                setPdfUrl("");
              }}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SaleQuote;
